import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { LocalizationPipe, LocalizationService, NgxValidateCoreModule } from '@abp/ng.core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { CxSelectComponent } from 'src/app/shared/components/cx-select/cx-select.component';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { CxInputPendingLoaderDirective } from 'src/app/shared/directives/cx-input-pending-loader.directive';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { CrudService } from 'src/core/services/crud/crud.service';
import { StateDefinitionDto } from 'src/core/models/state-definition/state-definition.dto';
import { StateType } from 'src/core/models/state-definition/state-type.enum';
import { StateDefinitionService } from '../../services/state-definition.service';
import { StateDefinitionCacheService } from 'src/core/services/state-definition/state-definition-cache.service';

@Component({
  selector: 'app-state-definition-form-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LocalizationPipe,
    DialogModule,
    InputTextModule,
    CxSelectComponent,
    NgxValidateCoreModule,
    CxInputPendingLoaderDirective,
    FormActionsComponent,
  ],
  templateUrl: './state-definition-form-panel.component.html',
  styleUrl: './state-definition-form-panel.component.scss',
})
export class StateDefinitionFormPanelComponent extends BaseFormComponent<StateDefinitionDto> {
  private readonly fb = inject(FormBuilder);
  private readonly crudService = inject(CrudService);
  private readonly localization = inject(LocalizationService);
  private readonly stateCache = inject(StateDefinitionCacheService);
  private currentStatusName = '';

  @Input() visible = false;
  @Input() statusId: number | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  readonly typeOptions = [
    {
      value: StateType.Work,
      label: this.localization.instant('Settings::WorkEnvironment:StatusManagement:Work').trim(),
    },
    {
      value: StateType.Break,
      label: this.localization.instant('Settings::WorkEnvironment:StatusManagement:Break').trim(),
    },
  ];

  constructor(
    private readonly stateDefinitionService: StateDefinitionService,
    changeDetectorRef: ChangeDetectorRef,
  ) {
    super(changeDetectorRef);
  }

  ngOnChanges(): void {
    this.syncFormFromInputs();
  }

  get isEditMode(): boolean {
    return this.statusId != null;
  }

  get isValidating(): boolean {
    return this.form?.pending ?? false;
  }

  get isBusy(): boolean {
    return this.isSubmitting || this.isValidating;
  }

  get headerKey(): string {
    return this.isEditMode
      ? 'Settings::WorkEnvironment:StatusManagement:EditPanelTitle'
      : 'Settings::WorkEnvironment:StatusManagement:AddPanelTitle';
  }

  protected buildForm(): FormGroup {
    return this.fb.group({
      name: this.fb.control('', {
        validators: [Validators.required, Validators.maxLength(100)],
        asyncValidators: [this.uniqueNameValidator()],
        updateOn: 'change',
      }),
      typeId: this.fb.control<StateType | null>(null, {
        validators: [Validators.required, this.typeAllowedValidator()],
      }),
    });
  }

  protected override afterFormInit(): void {
    this.syncFormFromInputs();
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  onCancel(): void {
    this.visibleChange.emit(false);
  }

  onSave(): void {
    if (this.isValidating) {
      return;
    }

    this.onSubmit();
  }

  protected submitRequest(value: StateDefinitionDto): Observable<unknown> {
    const selectedTypeId = this.form.getRawValue().typeId ?? StateType.Work;

    const payload = {
      name: value.name.trim(),
      isActive: true,
      typeId: selectedTypeId,
    };

    if (this.statusId != null) {
      const updatePayload: { id: number | string } = {
        id: this.statusId,
      };
      Object.assign(updatePayload, payload);

      return this.crudService.update<StateDefinitionDto>(StateDefinitionDto, updatePayload);
    }

    return this.crudService.insertWithoutId(StateDefinitionDto, {
      ...payload,
    });
  }

  protected override onSubmitSuccess(): void {
    super.onSubmitSuccess();
    this.stateCache.refresh();
    this.saved.emit();
    this.visibleChange.emit(false);
  }

  private uniqueNameValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = String(control.value ?? '').trim();
      if (!value) {
        return of(null);
      }

      if (
        this.statusId != null &&
        value.toLocaleLowerCase() === this.currentStatusName.toLocaleLowerCase()
      ) {
        return of(null);
      }

      return timer(300).pipe(
        switchMap(() =>
          this.stateDefinitionService.checkNameUnique(value, this.statusId ?? undefined),
        ),
        map(isUnique => (isUnique ? null : { nameNotUnique: true })),
        catchError(() => of(null)),
        take(1),
      );
    };
  }

  private syncFormFromInputs(): void {
    if (!this.visible) {
      return;
    }

    if (this.statusId == null) {
      this.currentStatusName = '';
      this.resetForm({
        name: '',
        typeId: null,
      });
      this.form.get('typeId')?.enable({ emitEvent: false });
      return;
    }

    this.crudService.getById<StateDefinitionDto>(StateDefinitionDto, this.statusId).subscribe({
      next: status => {
        const name = status?.name ?? '';
        this.currentStatusName = name.trim();
        this.resetForm({
          name,
          typeId: status?.typeId ?? StateType.Work,
        });
        this.form.get('typeId')?.disable({ emitEvent: false });
      },
      error: () => {
        this.currentStatusName = '';
        this.resetForm({
          name: '',
          typeId: null,
        });
        this.form.get('typeId')?.enable({ emitEvent: false });
      },
    });
  }

  private typeAllowedValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value == null) {
        return null;
      }

      const value = Number(control.value);

      if (value === StateType.Work || value === StateType.Break) {
        return null;
      }

      return { invalidType: true };
    };
  }
}
